import styled from '@emotion/styled'
import moment from 'moment'
import * as React from 'react'
import { Query } from 'react-apollo'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import useAppFeatureFlags from 'main/hooks/useAppFeatureFlags'
import useSSOContext from 'main/hooks/useSSOContext'
import { breakpoints, spacing } from 'main/ui'
import { DATE_FORMAT } from 'main/util/date'

import {
  SchoolPage_school as School,
  SchoolPage_school_featureFlags as FeatureFlag,
} from '../../gql'
import { AddToFavorites, Enroll, MessageDirector, ScheduleTour } from '../Content'
import RequestATour from '../Content/RequestATour'
import {
  SchoolPageScheduleTourButtonQuery,
  SchoolPageScheduleTourButtonQueryVariables,
} from './gql/__generated__/SchoolPageScheduleTourButtonQuery'
import { SCHOOL_PAGE_SCHEDULE_TOUR_BUTTON_QUERY } from './gql/gql'

export const CTASectionContainer = styled.div`
  display: flex;
  flex-direction: row;

  > :not(:last-child) {
    margin-right: ${spacing.minorScale(1)}px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    margin-right: ${spacing.minorScale(1)}px;
  }
`

interface IProps extends RouteComponentProps {
  full: boolean
  messageDirectorLabel: string
  scheduleTourLabel: string
  school: School
}

function CTASection(props: IProps) {
  const { full, messageDirectorLabel, scheduleTourLabel, school, location } = props
  const { extendNavContext } = useSSOContext()
  const { appFeatureFlagEnabled } = useAppFeatureFlags()
  const isRequestATourReleased = appFeatureFlagEnabled('request_a_tour')
  const showForCcms2 = appFeatureFlagEnabled('ccms_add_student_redirect')
  const hideApplyToEnrollButtonFlag =
    school.featureFlags &&
    school.featureFlags.find(featureFlag => featureFlag.id === 'hide_apply_to_enroll_button')
  const hideApplyToEnrollButton = hideApplyToEnrollButtonFlag
    ? hideApplyToEnrollButtonFlag.enabled
    : false

  React.useEffect(() => {
    extendNavContext({
      route: location.pathname,
    })
  }, [])

  return (
    <Query<SchoolPageScheduleTourButtonQuery, SchoolPageScheduleTourButtonQueryVariables>
      variables={queryVariables()}
      query={SCHOOL_PAGE_SCHEDULE_TOUR_BUTTON_QUERY}
    >
      {({ data, error }) => {
        const disabled = !error && !!data && !!data.tourOpenings && !data.tourOpenings.length
        const hasTourRequests =
          !error && !!data && !!data.tourRequests && !!data.tourRequests.length
        const isLoggedIn = !error && !!data && !!data.user && !!data.user.id

        return (
          <>
            {!hideApplyToEnrollButton && (
              <Enroll disabled={disabled} full={full} school={school} showForCcms2={showForCcms2} />
            )}
            {maybeRenderTourFeature(disabled, hasTourRequests, isLoggedIn)}
            <MessageDirector
              label={messageDirectorLabel}
              schoolId={school.id}
              full={full}
              tourDisabled={disabled}
            />
            <AddToFavorites schoolId={school.id} full={full} />
          </>
        )
      }}
    </Query>
  )

  function maybeRenderTourFeature(
    hasTourOpenings: boolean,
    hasTourRequests: boolean,
    isLoggedIn: boolean,
  ) {
    if (isRequestATourReleased && isRequestATourEnabled()) {
      return (
        <RequestATour
          schoolId={school.id}
          hasTourRequests={hasTourRequests}
          isLoggedIn={isLoggedIn}
        />
      )
    } else {
      return (
        <ScheduleTour
          label={scheduleTourLabel}
          schoolId={school.id}
          full={full}
          disabled={hasTourOpenings}
        />
      )
    }
  }

  function queryVariables(): SchoolPageScheduleTourButtonQueryVariables {
    return {
      beginsOn: moment().format(DATE_FORMAT),
      endsOn: moment().add(4, 'weeks').format(DATE_FORMAT),
      schoolId: `${school.id}`,
      schoolIdInt: school.id,
    }
  }

  function isRequestATourEnabled() {
    if (!school.featureFlags) {
      return {}
    }

    return school.featureFlags.some(
      (flag: FeatureFlag) => flag.id === 'school_request_a_tour' && !!flag.enabled,
    )
  }
}

export default withRouter(CTASection)
